<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  run: { type: Object, required: true },
});

defineEmits(['cancel']);

const { t } = useI18n();

const isRunning = computed(() =>
  ['pending', 'processing'].includes(props.run.status)
);

// "0 of 0" for a run stopped before the walk began says nothing; name that state instead.
const neverStarted = computed(
  () => props.run.status === 'cancelled' && props.run.total_count === 0
);

const formattedDate = computed(() =>
  new Date(props.run.completed_at || props.run.created_at).toLocaleString()
);

const generatedLabel = computed(() =>
  props.run.initiated_by
    ? t('ANALYSIS.QUALITY.RUN_CARD.GENERATED', {
        date: formattedDate.value,
        name: props.run.initiated_by.name,
      })
    : t('ANALYSIS.QUALITY.RUN_CARD.GENERATED_SYSTEM', {
        date: formattedDate.value,
      })
);

const statusColor = computed(() => {
  if (props.run.status === 'failed') return 'text-n-ruby-11';
  if (props.run.status === 'cancelled') return 'text-n-amber-11';
  return 'text-n-slate-11';
});
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
  >
    <div class="flex items-start justify-between gap-4">
      <div class="flex flex-col">
        <span class="text-sm font-medium text-n-slate-12">
          {{
            isRunning
              ? t('ANALYSIS.QUALITY.RUN_CARD.RUNNING')
              : run.analyzer_name
          }}
        </span>
        <span class="text-sm" :class="statusColor">
          <template v-if="run.status === 'cancelled'">
            {{
              neverStarted
                ? t('ANALYSIS.QUALITY.RUN_CARD.NEVER_STARTED')
                : t('ANALYSIS.QUALITY.RUN_CARD.CANCELLED')
            }}
          </template>
          <template v-else-if="run.status === 'failed'">
            {{ t('ANALYSIS.QUALITY.RUN_CARD.FAILED_STATE') }}
            <template v-if="run.error_message">
              {{ ` — ${run.error_message}` }}
            </template>
          </template>
          <template v-else>{{ generatedLabel }}</template>
        </span>
      </div>
      <NextButton
        v-if="isRunning"
        slate
        faded
        sm
        :label="t('ANALYSIS.QUALITY.RUN_CARD.CANCEL')"
        @click="$emit('cancel', run)"
      />
    </div>

    <div v-if="isRunning" class="flex flex-col gap-1.5">
      <div class="h-1.5 rounded-full bg-n-alpha-2 overflow-hidden">
        <div
          class="h-full rounded-full bg-n-brand transition-all duration-500"
          :style="{ width: `${run.progress_percentage}%` }"
        />
      </div>
      <span class="text-xs text-n-slate-11 tabular-nums">
        {{
          t('ANALYSIS.QUALITY.RUN_CARD.PROGRESS', {
            processed: run.processed_count,
            total: run.total_count,
          })
        }}
      </span>
    </div>

    <div
      v-else-if="!neverStarted"
      class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-11 tabular-nums"
    >
      <span>
        {{
          t('ANALYSIS.QUALITY.RUN_CARD.PROGRESS', {
            processed: run.processed_count,
            total: run.total_count,
          })
        }}
      </span>
      <span
        v-if="run.skipped_count"
        :title="t('ANALYSIS.QUALITY.RUN_CARD.SKIPPED_HINT')"
      >
        {{
          t('ANALYSIS.QUALITY.RUN_CARD.SKIPPED', { count: run.skipped_count })
        }}
      </span>
      <span v-if="run.failed_count" class="text-n-ruby-11">
        {{ t('ANALYSIS.QUALITY.RUN_CARD.FAILED', { count: run.failed_count }) }}
      </span>
      <span>
        {{
          t('ANALYSIS.QUALITY.RUN_CARD.CREDITS', { count: run.credits_used })
        }}
      </span>
    </div>
  </div>
</template>
